<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="flex items-center justify-between px-3 py-2 border-b border-slate-600 flex-shrink-0">
      <span class="text-xs font-semibold text-slate-300 uppercase tracking-wide">Synth Library</span>
      <div class="flex items-center gap-2">
        <button
          class="text-cyan-400 hover:text-cyan-300 text-xs font-medium px-2 py-1 rounded hover:bg-slate-600 transition-colors"
          title="Create synth"
          @click="createSynth"
        >+ New</button>
        <label
          class="text-cyan-400 hover:text-cyan-300 text-xs font-medium px-2 py-1 rounded hover:bg-slate-600 transition-colors cursor-pointer"
          title="Import a .synth, .mid, or .midi file"
        >
          + Import
          <input type="file" accept=".synth,.mid,.midi,text/plain,audio/midi,audio/x-midi" class="hidden" @change="onFileSelected" />
        </label>
      </div>
    </div>

    <div v-if="importError" class="px-3 py-1.5 text-xs text-red-400 border-b border-slate-700">{{ importError }}</div>
    <div v-if="deleteBlockedError" class="px-3 py-1.5 text-xs text-amber-300 border-b border-slate-700">{{ deleteBlockedError }}</div>
    <div v-if="importWarnings.length" class="border-b border-slate-700 px-3 py-1.5 text-xs text-amber-300">
      <div v-for="warning in importWarnings" :key="`${warning.code}:${warning.message}`">{{ warning.message }}</div>
    </div>

    <ResourceListToolbar
      v-if="synths.length > 0"
      :search="search"
      @update:search="search = $event"
      :sortKey="sortKey"
      @update:sortKey="sortKey = $event"
      :sortOptions="sortOptions"
    />

    <div class="flex-1 overflow-y-auto">
      <div v-if="synths.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">
        No synths yet.<br>
        Click <strong class="text-slate-400">+ New</strong> or <strong class="text-slate-400">+ Import</strong> to add one.
      </div>
      <div v-else-if="filteredSynths.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">
        No synths match your search.
      </div>

      <ResourceListItem
        v-for="s in filteredSynths"
        :key="s.uuid"
        :title="s.name"
        :meta="rowMeta(s)"
        :used="false"
        @select="openEditor(s.uuid)"
      >
        <template #actions>
          <button
            class="text-slate-400 hover:text-cyan-300 p-1 rounded hover:bg-slate-600 transition-colors"
            title="Duplicate"
            @click="duplicateSynth(s.uuid)"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            class="text-slate-400 hover:text-red-400 p-1 rounded hover:bg-slate-600 transition-colors"
            title="Delete"
            @click="requestDeleteSynth(s.uuid)"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </template>
      </ResourceListItem>
    </div>
  </div>

  <ConfirmationModal
    :show="pendingDeleteUUID !== null"
    title="Delete Synth"
    :message="pendingDeleteSynth ? `Are you sure you want to delete &lt;strong&gt;${pendingDeleteSynth.name}&lt;/strong&gt;?` : 'Are you sure?'"
    @confirm="confirmDelete"
    @cancel="pendingDeleteUUID = null"
  />

  <FloatingPopup
    id="synth-editor"
    :modelValue="editingUUID !== null && editingSynth !== null"
    @update:modelValue="v => { if (!v) editingUUID = null }"
    :title="editingSynth?.name || 'Synth'"
    :width="1100"
    :height="760"
    :minwidth="900"
    :minheight="620"
    :position="{ x: 120, y: 40 }"
    contentClass="overflow-hidden"
  >
    <SynthEditor
      v-if="editingSynth"
      :synth="editingSynth"
      :settings="settings"
      @close="editingUUID = null"
      @rename="onRename"
      @export="exportSynth"
    />
  </FloatingPopup>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useSynthStore } from '../../stores/synthStore.js'
import { useAudioStore } from '../../stores/audioStore.js'
import { serializeSynth } from '../../stores/synthFormat.js'
import FloatingPopup from '../FloatingPopup.vue'
import ConfirmationModal from '../ConfirmationModal.vue'
import ResourceListToolbar from '../resources/ResourceListToolbar.vue'
import ResourceListItem from '../resources/ResourceListItem.vue'
import SynthEditor from './SynthEditor.vue'

const props = defineProps({
  requestOpenUUID: { type: String, default: null },
  settings: { type: Object, default: null },
})

const store = useSynthStore()
const audioStore = useAudioStore()
const synths = computed(() => store.all)

const search = ref('')
const sortKey = ref('name-asc')
const sortOptions = [
  { value: 'name-asc', label: 'Name ↑' },
  { value: 'name-desc', label: 'Name ↓' },
]

const filteredSynths = computed(() => {
  let list = synths.value
  const q = search.value.trim().toLowerCase()
  if (q) {
    list = list.filter(s => s.name.toLowerCase().includes(q) || (s.filename ?? '').toLowerCase().includes(q))
  }
  list = [...list]
  switch (sortKey.value) {
    case 'name-asc': list.sort((a, b) => a.name.localeCompare(b.name)); break
    case 'name-desc': list.sort((a, b) => b.name.localeCompare(a.name)); break
  }
  return list
})

function rowMeta(s) {
  const activeTracks = s.tracks.filter(track => track.enabled).length
  const totalEvents = s.tracks.reduce((n, track) => n + (track.sequence?.length ?? 0), 0)
  return [s.filename || 'no file', `${activeTracks} active tracks`, `${totalEvents} events`]
}

const editingUUID = ref(null)
const editingSynth = computed(() => editingUUID.value ? store.getSynth(editingUUID.value) : null)

watch(() => props.requestOpenUUID, (uuid) => {
  if (uuid) editingUUID.value = uuid
})

function createSynth() {
  const uuid = store.addSynth()
  editingUUID.value = uuid
}

function openEditor(uuid) {
  editingUUID.value = uuid
}

function duplicateSynth(uuid) {
  const newUUID = store.duplicateSynth(uuid)
  if (newUUID) editingUUID.value = newUUID
}

const pendingDeleteUUID = ref(null)
const pendingDeleteSynth = computed(() => pendingDeleteUUID.value ? store.getSynth(pendingDeleteUUID.value) : null)
const deleteBlockedError = ref('')

function requestDeleteSynth(uuid) {
  const references = audioStore.all.filter(resource => resource.type === 'synth' && resource.synthId === uuid)
  if (references.length) {
    const synth = store.getSynth(uuid)
    deleteBlockedError.value = `Cannot delete "${synth?.name || 'this synth'}" while ${references.length} Audio Resource reference${references.length === 1 ? '' : 's'} ${references.length === 1 ? 'is' : 'are'} using it. Remove the Synth reference from Audio Resources first.`
    return
  }
  deleteBlockedError.value = ''
  pendingDeleteUUID.value = uuid
}

function confirmDelete() {
  const uuid = pendingDeleteUUID.value
  if (!store.removeSynth(uuid)) {
    requestDeleteSynth(uuid)
    pendingDeleteUUID.value = null
    return
  }
  pendingDeleteUUID.value = null
  if (editingUUID.value === uuid) editingUUID.value = null
}

function onRename({ uuid, name, filename }) {
  const patch = { name }
  if (filename != null) patch.filename = filename
  store.updateSynth(uuid, patch)
}

function exportSynth(synth) {
  const text = serializeSynth(synth)
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = synth.filename || 'new-synth.synth'
  link.click()
  URL.revokeObjectURL(url)
}

async function onFileSelected(event) {
  const file = event.target.files?.[0]
  if (!file) return
  importError.value = ''
  importWarnings.value = []
  try {
    editingUUID.value = await store.importSynthFile(file)
    importWarnings.value = store.lastImportWarnings
  } catch (e) {
    importError.value = e?.message || 'Failed to import synth.'
  }
  event.target.value = ''
}

const importError = ref('')
const importWarnings = ref([])
</script>
